import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { getApiUrl } from '../../utils/urls';

const initialState = {
  products: [],
  product: null,
  categories: [],
  stats: null,
  loading: false,
  error: null,
  status: 'idle',
};

export const fetchProducts = createAsyncThunk(
  'products/fetchProducts',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(getApiUrl('/products'));
      console.log('[DEBUG API] Réponse brute /products:', data);
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response && error.response.data.message
          ? error.response.data.message
          : error.message
      );
    }
  }
);

export const fetchProductDetails = createAsyncThunk(
  'products/fetchProductDetails',
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(getApiUrl(`/products/${id}`));
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response && error.response.data.message
          ? error.response.data.message
          : error.message
      );
    }
  }
);

// Actions Admin pour la gestion des prix
export const fetchAdminProducts = createAsyncThunk(
  'products/fetchAdminProducts',
  async (params = {}, { rejectWithValue, getState }) => {
    try {
      const { userInfo } = getState().auth;
      const config = {
        headers: {
          Authorization: `Bearer ${userInfo.token}`,
        },
      };

      const queryParams = new URLSearchParams();
      if (params.search) queryParams.append('search', params.search);
      if (params.category) queryParams.append('category', params.category);
      if (params.page) queryParams.append('page', params.page);
      if (params.limit) queryParams.append('limit', params.limit);

      const { data } = await axios.get(
        getApiUrl(`/admin/products?${queryParams.toString()}`),
        config
      );
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response && error.response.data.message
          ? error.response.data.message
          : error.message
      );
    }
  }
);

export const fetchProductStats = createAsyncThunk(
  'products/fetchProductStats',
  async (_, { rejectWithValue, getState }) => {
    try {
      const { userInfo } = getState().auth;
      const config = {
        headers: {
          Authorization: `Bearer ${userInfo.token}`,
        },
      };

      const { data } = await axios.get(getApiUrl('/admin/products/stats'), config);
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response && error.response.data.message
          ? error.response.data.message
          : error.message
      );
    }
  }
);

export const updateProductPrice = createAsyncThunk(
  'products/updateProductPrice',
  async ({ productId, price, reason }, { rejectWithValue, getState }) => {
    try {
      const { userInfo } = getState().auth;
      const config = {
        headers: {
          Authorization: `Bearer ${userInfo.token}`,
        },
      };

      const { data } = await axios.put(
        getApiUrl(`/admin/products/${productId}/price`),
        { price, reason },
        config
      );
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response && error.response.data.message
          ? error.response.data.message
          : error.message
      );
    }
  }
);

export const updateProductLicenseRequirement = createAsyncThunk(
  'products/updateProductLicenseRequirement',
  async ({ productId, requiresLicense }, { rejectWithValue, getState }) => {
    try {
      const { userInfo } = getState().auth;
      const config = {
        headers: {
          Authorization: `Bearer ${userInfo.token}`,
        },
      };

      const { data } = await axios.put(
        getApiUrl(
          `/products/${encodeURIComponent(String(productId))}/license`
        ),
        { requiresLicense },
        config
      );
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response && error.response.data.message
          ? error.response.data.message
          : error.message
      );
    }
  }
);

export const bulkUpdatePrices = createAsyncThunk(
  'products/bulkUpdatePrices',
  async (updates, { rejectWithValue, getState }) => {
    try {
      const { userInfo } = getState().auth;
      const config = {
        headers: {
          Authorization: `Bearer ${userInfo.token}`,
        },
      };

      const { data } = await axios.put(
        getApiUrl('/admin/products/bulk-price-update'),
        { updates },
        config
      );
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response && error.response.data.message
          ? error.response.data.message
          : error.message
      );
    }
  }
);

const productsSlice = createSlice({
  name: 'products',
  initialState,
  reducers: {
    clearProductDetails: (state) => {
      state.product = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Actions publiques existantes
      .addCase(fetchProducts.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.status = 'loading';
      })
      .addCase(fetchProducts.fulfilled, (state, action) => {
        state.loading = false;
        state.status = 'succeeded';
        state.products = action.payload;
      })
      .addCase(fetchProducts.rejected, (state, action) => {
        state.loading = false;
        state.status = 'failed';
        state.error = action.payload;
      })
      .addCase(fetchProductDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.status = 'loading';
      })
      .addCase(fetchProductDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.status = 'succeeded';
        state.product = action.payload;
      })
      .addCase(fetchProductDetails.rejected, (state, action) => {
        state.loading = false;
        state.status = 'failed';
        state.error = action.payload;
      })
      // Actions Admin pour la gestion des prix
      .addCase(fetchAdminProducts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAdminProducts.fulfilled, (state, action) => {
        state.loading = false;
        state.products = action.payload.products;
        state.categories = action.payload.categories;
      })
      .addCase(fetchAdminProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchProductStats.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProductStats.fulfilled, (state, action) => {
        state.loading = false;
        state.stats = action.payload;
      })
      .addCase(fetchProductStats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateProductPrice.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateProductPrice.fulfilled, (state, action) => {
        state.loading = false;
        // Mettre à jour le produit dans la liste
        if (state.products && Array.isArray(state.products)) {
          const index = state.products.findIndex(p => p._id === action.payload.product._id);
          if (index !== -1) {
            state.products[index] = action.payload.product;
          }
        }
      })
      .addCase(updateProductPrice.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateProductLicenseRequirement.fulfilled, (state, action) => {
        const updated = action.payload?.product;
        if (updated && state.products && Array.isArray(state.products)) {
          const index = state.products.findIndex(
            (p) => String(p._id) === String(updated._id)
          );
          if (index !== -1) {
            state.products[index] = updated;
          }
        }
      })
      .addCase(updateProductLicenseRequirement.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(bulkUpdatePrices.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(bulkUpdatePrices.fulfilled, (state, action) => {
        state.loading = false;
        // Recharger les produits après mise à jour groupée
        if (action.payload.results) {
          action.payload.results.forEach(result => {
            if (result.productId && state.products && Array.isArray(state.products)) {
              const index = state.products.findIndex(p => p._id === result.productId);
              if (index !== -1 && result.productName) {
                // Mettre à jour le prix localement
                state.products[index].price = result.newPrice;
                state.products[index].updatedAt = new Date().toISOString();
              }
            }
          });
        }
      })
      .addCase(bulkUpdatePrices.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearProductDetails } = productsSlice.actions;

export const selectProductById = (state, productId) => 
  state.products.products.find(product => product._id === productId);

export default productsSlice.reducer; 